import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button, Card, Field, Notice } from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * Accepting an invitation.
 *
 * A pasted code rather than a link, because there is no messaging adapter until
 * Slice 4 and the organizer passes the code on by hand. The page says so instead
 * of implying an email is on its way.
 */
export function AcceptInvitationPage() {
  const navigate = useNavigate();
  const { reload } = useSession();
  const [token, setToken] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.request<{ organizationId: string }>(
        '/api/v1/auth/invitations/accept',
        { method: 'POST', body: { invitationToken: token.trim() } },
      );
      await reload();
      void navigate(`/o/${result.organizationId}`, { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="gc-auth">
      <div className="gc-auth__card gc-stack">
        <Card>
          <form className="gc-stack" onSubmit={(event) => void submit(event)}>
            <h1>Accept an invitation</h1>

            <Notice tone="info" title="Invitations are handed over directly">
              Messaging is not live in this release, so whoever invited you will have passed you the
              code themselves.
            </Notice>

            <FailureNotice error={error} />

            <Field
              label="Invitation code"
              name="invitationToken"
              required
              hint="It must have been sent to the email address you signed in with."
              value={token}
              onChange={(event) => setToken(event.target.value)}
            />

            <Button type="submit" variant="primary" size="large" block busy={busy}>
              Join organization
            </Button>
          </form>
        </Card>
        <p className="gc-muted">
          <Link to="/organizations">Back to your organizations</Link>
        </p>
      </div>
    </main>
  );
}
